import React, { useEffect, useState } from "react";
import { Table, Input, Button } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import { Edit, Trash2 } from "lucide-react";
import { getRequest, deleteRequest } from "../../../../service/index";
import { getEmployeeData } from "../../../../utils/localStorage";
import ModalWrapper from "../../../../Modal";
import AddExtra from "../../../../modals/addExtra";
import EditExtra from "../../../../modals/editExtra";
import { countries } from '../../../../constant/countery';
import { Modal, message } from "antd";

const CompensationInfo = () => {
  const [data, setData] = useState([]);
  const [search, setSearch] = useState("");
  const [addNew, setAddNew] = useState(false);
  const [editModal, setEditModal] = useState(false); // NEW
  const [selectedRow, setSelectedRow] = useState(null); // NEW
  const [loading, setLoading] = useState(false);

  const userData = getEmployeeData();
  const currency = userData?.currency;
  const country = countries.find(c => c.currency === currency);
  const currencySign = country ? country.currencySymbol : currency;

  // Permission checks
  const userRole = userData?.role;
  const compensationPermissions = userData?.designation?.permissions?.Payroll?.['Compensation Info'] || [];
  
  const canView = userRole === "companyadmin" || compensationPermissions.includes("view");
  const canAdd = userRole === "companyadmin" || compensationPermissions.includes("add");
  const canEdit = userRole === "companyadmin" || compensationPermissions.includes("edit");
  const canDelete = userRole === "companyadmin" || compensationPermissions.includes("delete");

  const columns = [
    { title: "Title", dataIndex: "title", key: "title" },
    { 
      title: "Type", 
      dataIndex: "type", 
      key: "type",
      render: (type) => {
        if (type === "deductions") return "Deduction";
        if (type === "allowances") return "Allowance";
        if (type === "contributions") return "Contribution";
        return type;
      }
    },
    { 
      title: "Amount", 
      dataIndex: "amount", 
      key: "amount",
      render: (amount, record) => {
        if (record.amountType === "percentage") {
          return `${amount}%`;
        } else {
          return `${currencySign} ${amount}`;
        }
      }
    },
    { 
      title: "Amount Type", 
      dataIndex: "amountType", 
      key: "amountType",
      render: (amountType) => 
        amountType === "percentage" ? "Percentage" : "Price"
    },
    { 
      title: "Action",
      key: "action",
      render: (_, record) => (
        <div className="flex gap-3">
          <Button
            type="text"
            icon={<Edit className="w-4 h-4" />}
            disabled={!canEdit}
            onClick={() => {
              if (!canEdit) {
                message.warning("You don't have permission to edit compensation info.");
                return;
              }
              setSelectedRow(record);
              setEditModal(true);
            }}
            className={canEdit ? "text-blue-600 hover:text-blue-800" : "text-gray-400 cursor-not-allowed"}
            title={canEdit ? "Edit" : "You don't have permission to edit"}
          />
          <Button
            type="text"
            icon={<Trash2 className="w-4 h-4" />}
            disabled={!canDelete}
            onClick={() => handleDelete(record)}
            className={canDelete ? "text-red-600 hover:text-red-800" : "text-gray-400 cursor-not-allowed"}
            title={canDelete ? "Delete" : "You don't have permission to delete"}
          />
        </div>
      )
    }
  ];

  useEffect(() => {
    fetchData();
  }, [canView]);

  const fetchData = async () => {
    try {
      setLoading(true);
      if (!canView) {
        setData([]);
        return;
      }
      const response = await getRequest("/api/taxandbonus");
      if (response && Array.isArray(response.entries)) {
        setData(response.entries.map(item => ({
          ...item,
          key: item._id
        })));
      }
    } catch (error) {
      // Optionally handle error
    } finally {
      setLoading(false);
    }
  };

  const filteredData = data.filter(item =>
    item.title?.toLowerCase().includes(search.toLowerCase())
  );

  const onExtraClose = () => { setAddNew(false);  fetchData(); };
  const onEditClose = () => { setEditModal(false); setSelectedRow(null); };
  const onEditSuccess = () => { setEditModal(false); setSelectedRow(null); fetchData(); };

  const handleDelete = (record) => {
    if (!canDelete) {
      message.warning("You don't have permission to delete compensation info.");
      return;
    }

    Modal.confirm({
      title: 'Are you sure you want to delete this entry?',
      content: `Title: ${record.title}`,
      okText: 'Yes',
      okType: 'danger',
      cancelText: 'No',
      onOk: async () => {
        try {
          await deleteRequest(`/api/taxandbonus?entryId=${record._id}`);
          message.success('Entry deleted successfully');
          fetchData();
        } catch (error) {
          message.error('Failed to delete entry');
        }
      },
    });
  };

  return (
    <div className="p-3 shadow-[0_4px_6px_rgba(0,0,0,0.1),0_-4px_6px_rgba(0,0,0,0.05)] rounded-md">
      <div className="flex justify-between items-center">
        <div className="flex w-full max-w-md gap-4 items-center">
          <Input 
            placeholder="Search" 
            className="w-full text-md" 
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Button
          type="primary"
          className="bg-primary text-white"
          disabled={!canAdd}
          onClick={() => canAdd ? setAddNew(true) : message.warning("You don't have permission to add compensation info.")}
        >
          <PlusOutlined /> Create a New
        </Button>
      </div>

      {/* Table */}
      <div className="rounded mt-4">
        <Table
          columns={columns}
          dataSource={canView ? filteredData : []}
          locale={{ emptyText: "No Data" }}
          scroll={{ x: 'max-content' }}
          loading={loading}
          pagination={{
            total: canView ? filteredData.length : 0,
            pageSize: 10,
            showSizeChanger: true,
            showTotal: (total) => `Total ${total} items`,
          }}
          className="bg-white rounded-lg border border-gray-200"
        />
        {!canView && (
          <div className="text-center text-gray-500 py-4">You don't have permission to view compensation info.</div>
        )}
      </div>

      <ModalWrapper
        open={addNew}
        title="Add New Extra"
        onRequestClose={onExtraClose}
      >
        <AddExtra onClose={onExtraClose} />
      </ModalWrapper>

      {/* Edit Modal */}
      <ModalWrapper
        open={editModal}
        title="Edit Extra"
        onRequestClose={onEditClose}
      >
        {selectedRow && (
          <EditExtra data={selectedRow} onClose={onEditClose} onSuccess={onEditSuccess} />
        )}
      </ModalWrapper>
    </div>
  );
};

export default CompensationInfo;
